import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Customer, Referral, Review } from '@/lib/types';
import {
  Banner,
  Card,
  Field,
  Modal,
  ModalHeader,
  PageHeader,
  Stat,
  Tag,
  cellStyle,
  inputStyle,
  useToast,
} from '@/components/ui';
import { Icon, I } from '@/components/icons';
import { Select } from '@/components/Select';
import { CustomerPicker } from '@/components/CustomerPicker';
import { useSort, SortTh } from '@/components/sortable';
import { ntd } from '@/lib/format';

export function GrowthPage() {
  const qc = useQueryClient();
  const { msg, ok, fail } = useToast();
  const [reviewOpen, setReviewOpen] = useState(false);
  const [routeFilter, setRouteFilter] = useState('');
  const [review, setReview] = useState({ customerId: '', rating: 5, comment: '' });
  const [refOpen, setRefOpen] = useState(false);
  const [ref, setRef] = useState({ referrerCustomerId: '', newCustomerId: '', bonus: 500 });

  const customers = useQuery({ queryKey: ['customers'], queryFn: () => api.get<Customer[]>('/customers') });
  const reviews = useQuery({ queryKey: ['reviews'], queryFn: () => api.get<Review[]>('/engagement/reviews') });
  const referrals = useQuery({ queryKey: ['referrals'], queryFn: () => api.get<Referral[]>('/engagement/referrals') });

  const addReview = useMutation({
    mutationFn: () =>
      api.post('/engagement/reviews', {
        customerId: review.customerId,
        rating: Number(review.rating),
        comment: review.comment || undefined,
      }),
    onSuccess: () => {
      ok('評價已記錄並自動路由');
      setReviewOpen(false);
      setReview({ customerId: '', rating: 5, comment: '' });
      void qc.invalidateQueries({ queryKey: ['reviews'] });
    },
    onError: fail,
  });
  const addReferral = useMutation({
    mutationFn: () =>
      api.post('/engagement/referrals', {
        referrerCustomerId: ref.referrerCustomerId,
        newCustomerId: ref.newCustomerId,
        bonusReferrerCents: Number(ref.bonus) * 100,
        bonusNewCents: Number(ref.bonus) * 100,
      }),
    onSuccess: () => {
      ok('推薦獎勵已建立');
      setRefOpen(false);
      setRef({ referrerCustomerId: '', newCustomerId: '', bonus: 500 });
      void qc.invalidateQueries({ queryKey: ['referrals'] });
    },
    onError: fail,
  });

  const reviewList = reviews.data ?? [];
  const shownReviews = routeFilter ? reviewList.filter((r) => r.route === routeFilter) : reviewList;
  const refList = referrals.data ?? [];
  const nameOf = (id: string) => customers.data?.find((c) => c.id === id)?.name ?? id;
  const avgRating = reviewList.length ? (reviewList.reduce((s, r) => s + r.rating, 0) / reviewList.length).toFixed(1) : '—';

  const sortReviews = useSort(shownReviews, {
    customer: (r) => nameOf(r.customerId),
    rating: (r) => r.rating,
    comment: (r) => r.comment ?? '',
    route: (r) => r.route,
  });
  const sortRefs = useSort(refList, {
    referrer: (r) => nameOf(r.referrerCustomerId),
    newCustomer: (r) => nameOf(r.newCustomerId),
    bonusReferrer: (r) => r.bonusReferrerCents,
    bonusNew: (r) => r.bonusNewCents,
  });

  return (
    <div>
      <PageHeader title="增長與轉介" subtitle="評價路由（≥4★ Google／<4★ 內部安撫）· 推薦獎勵" />
      <Banner msg={msg} />

      <div style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
        <Stat label="總評價" value={String(reviewList.length)} />
        <Stat label="平均星等" value={avgRating} tone="primary" />
        <Stat label="Google 公開" value={String(reviewList.filter((r) => r.route === 'GOOGLE').length)} tone="success" />
        <Stat label="推薦筆數" value={String(refList.length)} tone="wallet" />
      </div>

      <Card
        title="評價路由"
        icon={<Icon d={I.sparkle} size={14} style={{ color: 'var(--primary)' }} />}
        right={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ width: 140 }}>
              <Select value={routeFilter} onChange={setRouteFilter} placeholder="全部路由" options={[{ value: '', label: '全部路由' }, { value: 'GOOGLE', label: 'Google 公開' }, { value: 'INTERNAL', label: '內部安撫' }]} />
            </div>
            <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => setReviewOpen(true)}>
              <Icon d={I.plus} size={12} /> 記錄評價
            </button>
          </div>
        }
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <SortTh label="顧客" sortKey="customer" sort={sortReviews} />
              <SortTh label="評分" sortKey="rating" sort={sortReviews} />
              <SortTh label="留言" sortKey="comment" sort={sortReviews} />
              <SortTh label="路由" sortKey="route" sort={sortReviews} />
            </tr>
          </thead>
          <tbody>
            {sortReviews.sorted.map((r) => (
              <tr key={r.id}>
                <td style={cellStyle}>{nameOf(r.customerId)}</td>
                <td style={cellStyle} className="num">
                  {'★'.repeat(r.rating)}
                  <span style={{ color: 'var(--line)' }}>{'★'.repeat(5 - r.rating)}</span>
                </td>
                <td style={{ ...cellStyle, fontSize: 11, color: 'var(--ink-3)' }}>{r.comment || '—'}</td>
                <td style={cellStyle}>
                  {r.route === 'GOOGLE' ? <Tag tone="success" dot>Google 公開</Tag> : <Tag tone="warn">內部安撫</Tag>}
                </td>
              </tr>
            ))}
            {reviewList.length === 0 && (
              <tr>
                <td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={4}>
                  尚無評價
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      <Card
        title="推薦獎勵"
        icon={<Icon d={I.trend} size={14} style={{ color: 'var(--success)' }} />}
        right={
          <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => setRefOpen(true)}>
            <Icon d={I.plus} size={12} /> 新增推薦
          </button>
        }
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <SortTh label="推薦人" sortKey="referrer" sort={sortRefs} />
              <SortTh label="新客" sortKey="newCustomer" sort={sortRefs} />
              <SortTh label="推薦人獎勵" sortKey="bonusReferrer" sort={sortRefs} align="right" />
              <SortTh label="新客獎勵" sortKey="bonusNew" sort={sortRefs} align="right" />
            </tr>
          </thead>
          <tbody>
            {sortRefs.sorted.map((r) => (
              <tr key={r.id}>
                <td style={cellStyle}>{nameOf(r.referrerCustomerId)}</td>
                <td style={cellStyle}>{nameOf(r.newCustomerId)}</td>
                <td style={{ ...cellStyle, textAlign: 'right' }} className="num">
                  {ntd(r.bonusReferrerCents)}
                </td>
                <td style={{ ...cellStyle, textAlign: 'right' }} className="num">
                  {ntd(r.bonusNewCents)}
                </td>
              </tr>
            ))}
            {refList.length === 0 && (
              <tr>
                <td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={4}>
                  尚無推薦
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      <Modal open={reviewOpen} onClose={() => setReviewOpen(false)} width={440}>
        <ModalHeader title="記錄顧客評價" sub="≥4★ 導向 Google 公開，<4★ 轉內部安撫" onClose={() => setReviewOpen(false)} />
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="顧客 *">
            <CustomerPicker value={review.customerId} onChange={(id) => setReview((s) => ({ ...s, customerId: id }))} />
          </Field>
          <Field label="星等">
            <Select
              value={String(review.rating)}
              onChange={(v) => setReview((s) => ({ ...s, rating: Number(v) }))}
              options={[5, 4, 3, 2, 1].map((n) => ({ value: String(n), label: `${n} ★` }))}
            />
          </Field>
          <Field label="留言（選填）">
            <input style={inputStyle} value={review.comment} onChange={(e) => setReview((s) => ({ ...s, comment: e.target.value }))} placeholder="服務很滿意…" />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 8, padding: 16, borderTop: '1px solid var(--line)' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setReviewOpen(false)}>
            取消
          </button>
          <button className="btn btn-primary" style={{ flex: 1.5 }} disabled={!review.customerId || addReview.isPending} onClick={() => addReview.mutate()}>
            記錄並路由
          </button>
        </div>
      </Modal>

      <Modal open={refOpen} onClose={() => setRefOpen(false)} width={440}>
        <ModalHeader title="新增推薦獎勵" sub="推薦人與新客雙向獎勵" onClose={() => setRefOpen(false)} />
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="推薦人 *">
            <CustomerPicker value={ref.referrerCustomerId} onChange={(id) => setRef((s) => ({ ...s, referrerCustomerId: id }))} placeholder="搜尋推薦人…" />
          </Field>
          <Field label="新客 *">
            <CustomerPicker value={ref.newCustomerId} exclude={ref.referrerCustomerId} onChange={(id) => setRef((s) => ({ ...s, newCustomerId: id }))} placeholder="搜尋新客…" />
          </Field>
          <Field label="雙向獎勵金額（元）">
            <input type="number" style={inputStyle} className="num" value={ref.bonus} onChange={(e) => setRef((s) => ({ ...s, bonus: Number(e.target.value) }))} />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 8, padding: 16, borderTop: '1px solid var(--line)' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setRefOpen(false)}>
            取消
          </button>
          <button
            className="btn btn-primary"
            style={{ flex: 1.5 }}
            disabled={!ref.referrerCustomerId || !ref.newCustomerId || addReferral.isPending}
            onClick={() => addReferral.mutate()}
          >
            建立推薦
          </button>
        </div>
      </Modal>
    </div>
  );
}
